import { useState, useEffect, useCallback } from 'react';
import apiService from '../services/api';
import dataProcessor from '../services/dataProcessor';
import { createErrorMessage } from '../utils/helpers';

// Combined dashboard data hook
export const useDashboardData = (batchId) => {
  const [data, setData] = useState({
    batches: [],
    summary: null,
    chartData: null,
    mutations: [],
    geographicData: [],
    alerts: [],
    novelMutations: [],
    knownMutations: []
  });
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchAllData = useCallback(async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      // Always fetch batches
      const batchesPromise = apiService.getBatches();
      
      // Only fetch batch-specific data if batchId is provided
      const promises = [batchesPromise];
      
      if (batchId) {
        promises.push(
          apiService.getBatchSummary(batchId),
          apiService.getChartData(batchId),
          apiService.getNovelMutations(batchId),
          apiService.getKnownMutations(batchId),
          apiService.getGeographySummary({ batch_id: batchId }),
          apiService.getRealTimeAlerts({ batch_id: batchId, hours_back: 24 })
        );
      }

      const results = await Promise.allSettled(promises);
      
      // Process results
      const newData = { ...data };
      
      // Batches (always first)
      if (results[0].status === 'fulfilled' && results[0].value?.success) {
        newData.batches = dataProcessor.processBatchData(results[0].value.data || []);
      }

      if (batchId && results.length > 1) {
        // Summary
        if (results[1].status === 'fulfilled' && results[1].value?.success) {
          newData.summary = dataProcessor.processExecutiveSummary(results[1].value.data);
        }

        // Chart Data
        if (results[2].status === 'fulfilled' && results[2].value?.success) {
          newData.chartData = dataProcessor.processChartData(results[2].value.data);
          // Extract geographic data from chart data
          if (results[2].value.data?.geographic_distribution) {
            newData.geographicData = dataProcessor.processGeographicData(
              results[2].value.data.geographic_distribution
            );
          }
        }

        // Novel Mutations
        if (results[3].status === 'fulfilled' && results[3].value?.success) {
          newData.novelMutations = dataProcessor.processMutationsData(
            results[3].value.data?.novel_mutations || []
          );
        }

        // Known Mutations
        if (results[4].status === 'fulfilled' && results[4].value?.success) {
          newData.knownMutations = dataProcessor.processMutationsData(
            results[4].value.data?.mutations || []
          );
          // Combine mutations for the main mutations array
          newData.mutations = [...newData.novelMutations, ...newData.knownMutations];
        }

        // Geographic Data
        if (results[5].status === 'fulfilled' && results[5].value?.success) {
          newData.geographicData = dataProcessor.processGeographicData(
            results[5].value.data || []
          );
        }

        // Alerts
        if (results[6].status === 'fulfilled' && results[6].value?.success) {
          newData.alerts = dataProcessor.processAlerts(
            results[6].value.data?.alerts || []
          );
        }
      }

      setData(newData);
      setLastUpdated(new Date());
      
      // Check for any critical errors
      const errors = results
        .filter(result => result.status === 'rejected')
        .map(result => result.reason);
      
      if (errors.length > 0) {
        console.warn('Some data requests failed:', errors);
        // Only set error if all requests failed
        if (errors.length === results.length) {
          setError('Failed to load dashboard data');
        }
      }

    } catch (err) {
      console.error('Dashboard data fetch error:', err);
      setError(createErrorMessage(err));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [batchId, data]);

  const refreshData = useCallback(() => {
    fetchAllData(true);
  }, [fetchAllData]);

  // Initial data fetch
  useEffect(() => {
    fetchAllData(false);
  }, [batchId]); // Re-fetch when batchId changes

  return {
    ...data,
    loading,
    refreshing,
    error,
    lastUpdated,
    refreshData
  };
};

export default useDashboardData;